import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Bot, Headset, PlayCircle, Send, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Alert, Button, Dialog } from '@erp/ui';
import { api, ApiError, lerUltimoErro } from '../lib/api';
import { FormChamado } from './Suporte';

export interface ArtigoIndicado {
  slug: string;
  titulo: string;
  resumo: string;
  videoId: string | null;
}
export interface RespostaSuporte {
  id: string;
  resposta: string;
  tipo: 'duvida' | 'erro' | 'pediu_humano' | 'sem_resposta';
  encaminhar: boolean;
  artigos: ArtigoIndicado[];
}
interface Troca {
  pergunta: string;
  r: RespostaSuporte;
  util?: boolean;
}

/** Vídeo tutorial (YouTube não listado), sem cookies de rastreio até a pessoa dar play. */
export function VideoAjuda({ videoId, titulo }: { videoId: string; titulo: string }) {
  return (
    <div className="aspect-video w-full overflow-hidden rounded-lg border bg-muted">
      <iframe
        className="size-full"
        src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?rel=0`}
        title={`Vídeo: ${titulo}`}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

/** Texto da conversa para o chamado: a equipe vê o que já foi tentado. */
export function resumoConversa(trocas: Troca[]): string {
  return trocas
    .map((t) => `Pergunta: ${t.pergunta}\nAssistente: ${t.r.resposta}`)
    .join('\n\n')
    .slice(0, 4000);
}

/**
 * Suporte em duas camadas: o assistente responde dúvidas com base nos artigos e vídeos da Ajuda;
 * erro, pergunta sem resposta ou pedido de pessoa vai para a equipe com a conversa junto.
 */
export function AssistenteSuporte({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const { pathname } = useLocation();
  const [pergunta, setPergunta] = useState('');
  const [trocas, setTrocas] = useState<Troca[]>([]);
  const [paraEquipe, setParaEquipe] = useState(false);
  const fim = useRef<HTMLLIElement>(null);
  const perguntar = useMutation({
    mutationFn: (texto: string) =>
      api<RespostaSuporte>('/suporte/perguntar', {
        method: 'POST',
        json: {
          pergunta: texto,
          tela: pathname.slice(0, 200),
          ultimoErro: lerUltimoErro(),
          historico: trocas
            .slice(-3)
            .map((t) => ({ pergunta: t.pergunta, resposta: t.r.resposta })),
        },
      }),
    onSuccess: (r, texto) => {
      setTrocas((v) => [...v, { pergunta: texto, r }]);
      setPergunta('');
    },
  });
  const avaliar = useMutation({
    mutationFn: ({ id, util }: { id: string; util: boolean }) =>
      api(`/suporte/perguntas/${id}/avaliar`, { method: 'POST', json: { util } }),
    onMutate: ({ id, util }) =>
      setTrocas((v) => v.map((t) => (t.r.id === id ? { ...t, util } : t))),
  });
  useEffect(() => {
    fim.current?.scrollIntoView?.({ block: 'nearest' });
  }, [trocas.length]);

  const fechar = () => {
    setTrocas([]);
    setPergunta('');
    setParaEquipe(false);
    perguntar.reset();
    aoFechar();
  };
  const ultima = trocas.at(-1);
  const enviar = () => {
    const t = pergunta.trim();
    if (t.length >= 3 && !perguntar.isPending) perguntar.mutate(t);
  };

  return (
    <Dialog
      aberto={aberto}
      aoFechar={fechar}
      titulo={paraEquipe ? 'Enviar para a equipe' : 'Ajuda'}
      descricao={
        paraEquipe
          ? 'A conversa e a tela em que você está vão junto. Respondemos no mesmo dia, até as 22h.'
          : 'Pergunte do seu jeito. Se for um erro, a gente passa para a equipe.'
      }
    >
      {aberto && paraEquipe ? (
        <FormChamado
          mensagemInicial={resumoConversa(trocas)}
          perguntaSuporteId={ultima?.r.id}
          aoConcluir={fechar}
        />
      ) : (
        aberto && (
          <div className="flex flex-col gap-3">
            {trocas.length > 0 && (
              <ol className="flex max-h-[50vh] flex-col gap-3 overflow-y-auto" aria-live="polite">
                {trocas.map((t) => (
                  <li key={t.r.id} className="flex flex-col gap-2">
                    <p className="self-end rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
                      {t.pergunta}
                    </p>
                    <div className="flex flex-col gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
                      <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                        <Bot className="size-3.5" aria-hidden /> Assistente
                      </p>
                      <p className="whitespace-pre-line">{t.r.resposta}</p>
                      {t.r.artigos[0]?.videoId && (
                        <VideoAjuda
                          videoId={t.r.artigos[0].videoId}
                          titulo={t.r.artigos[0].titulo}
                        />
                      )}
                      {t.r.artigos.length > 0 && (
                        <ul className="flex flex-col gap-1">
                          {t.r.artigos.map((a) => (
                            <li key={a.slug}>
                              <Link
                                to={`/ajuda/${a.slug}`}
                                onClick={fechar}
                                className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                              >
                                {a.videoId && (
                                  <PlayCircle className="size-4" aria-label="Com vídeo" />
                                )}
                                {a.titulo}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                      {t.r.encaminhar ? (
                        <Button
                          size="sm"
                          className="self-start"
                          onClick={() => setParaEquipe(true)}
                        >
                          <Headset /> Enviar para a equipe
                        </Button>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          {t.util === undefined ? (
                            <>
                              Resolveu?
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7"
                                onClick={() => avaliar.mutate({ id: t.r.id, util: true })}
                              >
                                <ThumbsUp /> Sim
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7"
                                onClick={() => avaliar.mutate({ id: t.r.id, util: false })}
                              >
                                <ThumbsDown /> Não
                              </Button>
                            </>
                          ) : t.util ? (
                            'Que bom!'
                          ) : (
                            <>
                              Que pena.
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7"
                                onClick={() => setParaEquipe(true)}
                              >
                                <Headset /> Falar com uma pessoa
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
                <li ref={fim} aria-hidden />
              </ol>
            )}
            <form
              className="flex flex-col gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                enviar();
              }}
            >
              <label htmlFor="pergunta-suporte" className="text-sm font-medium">
                {trocas.length ? 'Outra dúvida?' : 'Qual é a sua dúvida?'}
              </label>
              <div className="flex items-end gap-2">
                <textarea
                  id="pergunta-suporte"
                  rows={2}
                  maxLength={1000}
                  value={pergunta}
                  onChange={(e) => setPergunta(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      enviar();
                    }
                  }}
                  placeholder="Ex.: como coloco um carro no site?"
                  className="min-w-0 flex-1 rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <Button
                  type="submit"
                  aria-label="Perguntar"
                  disabled={perguntar.isPending || pergunta.trim().length < 3}
                >
                  <Send />
                </Button>
              </div>
            </form>
            {perguntar.isPending && (
              <p className="text-sm text-muted-foreground" role="status">
                Procurando a resposta...
              </p>
            )}
            {perguntar.isError && (
              <Alert variant="erro">
                {perguntar.error instanceof ApiError
                  ? perguntar.error.message
                  : 'Não consegui responder agora.'}
              </Alert>
            )}
            <button
              type="button"
              onClick={() => setParaEquipe(true)}
              className="self-start text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Prefiro falar direto com uma pessoa
            </button>
          </div>
        )
      )}
    </Dialog>
  );
}
